import React, { useRef, useMemo } from 'react'
import { Box, Typography, Stack, Button, IconButton } from '@mui/material'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Swiper, SwiperSlide } from 'swiper/react'
import { EffectCoverflow, Autoplay, Navigation } from 'swiper/modules'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import LocationOnIcon from '@mui/icons-material/LocationOn'

import 'swiper/css'
import 'swiper/css/effect-coverflow'
import 'swiper/css/navigation'

import { projectsData } from '../Data/projectsData'
import bgVideo from '../Images/hero vedio.mp4'

// لون خلفية الصفحة - نفس لون سيكشن الـ Developers اللي قبله بالظبط (#2B2B2B)
const pageBg = '#2B2B2B'

// ارتفاع منطقة التلاشي - ثابت بالبكسل عشان يتزامن مع سيكشن الـ Developers
const FADE_HEIGHT = { xs: 140, md: 260 }

// عدد المشاريع المعروضة في السويبر
const PROJECTS_LIMIT = 6

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut', delay },
  }),
}

export const HomeLatestProjects = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const isArabic = (i18n.language || 'ar').startsWith('ar')
  const lang = isArabic ? 'ar' : 'en'

  const prevRef = useRef(null)
  const nextRef = useRef(null)

  // ✨ خد أول 6 مشاريع من projectsData
  const projects = useMemo(
    () => projectsData.slice(0, PROJECTS_LIMIT),
    []
  )

  return (
    <Box sx={{ position: 'relative', bgcolor: pageBg, py: { xs: 10, md: 14 }, overflow: 'hidden' }}>
      {/* خلفية الفيديو */}
      <Box
        component="video"
        autoPlay
        loop
        muted
        playsInline
        src={bgVideo}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0,
        }}
      />

      {/* طبقة تعتيم عامة فوق الفيديو */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: 'rgba(43,43,43,0.55)',
          zIndex: 1,
        }}
      />

      {/* ريشة التلاشي العلوية - اندماج مع سيكشن الـ Developers اللي فوق */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: FADE_HEIGHT,
          background: `linear-gradient(to bottom,
            ${pageBg} 0%,
            ${pageBg}E6 20%,
            ${pageBg}B3 40%,
            ${pageBg}66 65%,
            ${pageBg}1A 85%,
            transparent 100%)`,
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />

      {/* ريشة التلاشي السفلية - اندماج مع القسم اللي تحت */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: FADE_HEIGHT,
          background: `linear-gradient(to top,
            ${pageBg} 0%,
            ${pageBg}E6 20%,
            ${pageBg}B3 40%,
            ${pageBg}66 65%,
            ${pageBg}1A 85%,
            transparent 100%)`,
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 3 }}>
        {/* الهيدر */}
        <Box
          component={motion.div}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.6 }}
          sx={{
            maxWidth: 1400,
            mx: 'auto',
            px: { xs: 3, md: 6 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'flex-end' },
            gap: 3,
            mb: { xs: 6, md: 8 },
          }}
        >
          <Box>
            <Typography
              component={motion.p}
              custom={0}
              variants={fadeUp}
              variant="overline"
              sx={{ color: '#D9A5A5', fontWeight: 700, letterSpacing: 2, display: 'block' }}
            >
              {t('latestProjects.eyebrow')}
            </Typography>
            <Typography
              component={motion.h2}
              custom={0.1}
              variants={fadeUp}
              sx={{
                fontFamily: isArabic ? "'El Messiri', serif" : "'Bodoni Moda', serif",
                fontWeight: isArabic ? 700 : 500,
                fontSize: { xs: '2.2rem', md: '3rem' },
                color: '#D9D6CF',
                mt: 0.5,
                lineHeight: 1.1,
              }}
            >
              {t('latestProjects.title')}
            </Typography>
          </Box>

          <Box component={motion.div} custom={0.2} variants={fadeUp}>
            <Button
              onClick={() => navigate('/projects')}
              endIcon={<ArrowForwardIcon sx={{ transform: isArabic ? 'scaleX(-1)' : 'none' }} />}
              variant="contained"
              sx={{
                bgcolor: '#7A2E3A',
                color: '#D9D6CF',
                px: 4,
                py: 1.4,
                borderRadius: '999px',
                '&:hover': { bgcolor: '#8f3646' },
              }}
            >
              {t('latestProjects.cta')}
            </Button>
          </Box>
        </Box>

        {/* السويبر */}
        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: false, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          sx={{
            position: 'relative',
            width: '100%',
            '& .swiper': {
              pb: 2,
              overflow: 'visible',
              width: '100%',
              paddingInlineStart: '0 !important',
              paddingInlineEnd: '0 !important',
            },
            '& .swiper-wrapper': {
              marginLeft: 0,
            },
            '& .swiper-slide': {
              width: { xs: 300, sm: 380, md: 480 },
              transition: 'transform 0.3s ease, opacity 0.3s ease',
            },
          }}
        >
          <Swiper
            modules={[EffectCoverflow, Autoplay, Navigation]}
            effect="coverflow"
            grabCursor
            centeredSlides
            loop
            slidesPerView="auto"
            slidesOffsetBefore={0}
            slidesOffsetAfter={0}
            dir={isArabic ? 'rtl' : 'ltr'}
            key={isArabic ? 'rtl' : 'ltr'}
            coverflowEffect={{
              rotate: 0,
              stretch: 0,
              depth: 160,
              modifier: 1.4,
              slideShadows: false,
            }}
            autoplay={{
              delay: 3200,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
            onBeforeInit={(swiper) => {
              swiper.params.navigation.prevEl = prevRef.current
              swiper.params.navigation.nextEl = nextRef.current
            }}
            style={{ width: '100%', paddingTop: 20, paddingBottom: 20, paddingLeft: 0, paddingRight: 0 }}
          >
            {projects.map((project) => (
              <SwiperSlide key={project.id}>
                <Box
                  onClick={() => navigate(`/projects/${project.id}`)}
                  sx={{
                    borderRadius: '24px',
                    overflow: 'hidden',
                    position: 'relative',
                    height: { xs: 420, md: 560 },
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid rgba(217,214,207,0.15)',
                    boxShadow: '0 25px 50px rgba(0,0,0,0.45)',
                    cursor: 'pointer',
                  }}
                >
                  {/* الصورة - 70% من ارتفاع الكارت */}
                  <Box
                    component="img"
                    src={project.image}
                    alt={project.name[lang]}
                    loading="lazy"
                    sx={{
                      width: '100%',
                      height: '70%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />

                  {/* الجزء السفلي - خلفية زجاجية واضحة تبين الفيديو اللي وراها */}
                  <Stack
                    justifyContent="center"
                    spacing={0.8}
                    sx={{
                      height: '30%',
                      px: 2.5,
                      py: 2,
                      bgcolor: 'rgba(217,214,207,0.08)',
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      borderTop: '1px solid rgba(217,214,207,0.2)',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <LocationOnIcon sx={{ fontSize: 15, color: '#D9A5A5' }} />
                      <Typography sx={{ color: 'rgba(217,214,207,0.85)', fontSize: '0.8rem' }}>
                        {project.location[lang]}
                      </Typography>
                    </Stack>
                    <Typography
                      sx={{
                        fontFamily: isArabic ? "'El Messiri', serif" : "'Bodoni Moda', serif",
                        fontWeight: 600,
                        fontSize: '1.3rem',
                        color: '#FFFFFF',
                      }}
                    >
                      {project.name[lang]}
                    </Typography>
                    <Typography sx={{ color: '#D9A5A5', fontSize: '0.9rem', fontWeight: 600 }}>
                      {t('latestProjects.startingFrom')}{' '}
                      {project.price.toLocaleString()} {isArabic ? 'درهم' : 'AED'}
                    </Typography>
                  </Stack>
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* أسهم التنقل */}
          <IconButton
            ref={prevRef}
            sx={{
              position: 'absolute',
              top: '35%',
              left: { xs: 8, md: 24 },
              transform: 'translateY(-50%)',
              zIndex: 10,
              bgcolor: 'rgba(43,43,43,0.6)',
              border: '1px solid rgba(217,214,207,0.2)',
              color: '#D9D6CF',
              backdropFilter: 'blur(10px)',
              '&:hover': { bgcolor: '#7A2E3A' },
            }}
          >
            {isArabic ? <ArrowForwardIosIcon sx={{ fontSize: 18 }} /> : <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />}
          </IconButton>

          <IconButton
            ref={nextRef}
            sx={{
              position: 'absolute',
              top: '35%',
              right: { xs: 8, md: 24 },
              transform: 'translateY(-50%)',
              zIndex: 10,
              bgcolor: 'rgba(43,43,43,0.6)',
              border: '1px solid rgba(217,214,207,0.2)',
              color: '#D9D6CF',
              backdropFilter: 'blur(10px)',
              '&:hover': { bgcolor: '#7A2E3A' },
            }}
          >
            {isArabic ? <ArrowBackIosNewIcon sx={{ fontSize: 18 }} /> : <ArrowForwardIosIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>
      </Box>
    </Box>
  )
}